/**
 * Shared Dentally utility functions.
 * Shared Dentally utility functions for practitioner names, formatting, etc.
 */

// Parse Dentally "DD-MM-YYYY HH:mm:ss.SSSSSSZ" to native Date object
export const parseDentallyDate = (dateStr) => {
  if (!dateStr) return null;
  const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})\s*(.*)?$/);
  if (match) {
    const [, day, month, year, timePart] = match;
    const d = new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  try {
    const d = new Date(dateStr);
    if (!Number.isNaN(d.getTime())) return d;
  } catch { /* ignore */ }
  return null;
};

// Format Dentally datetime to readable string
export const formatDentallyDate = (dateStr, includeTime = false) => {
  const d = parseDentallyDate(dateStr);
  if (!d) return dateStr || null;
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  if (includeTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
  return d.toLocaleDateString('en-GB', opts);
};

// Format diary time "HH:mm"
export const formatDiaryTime = (dateStr) => {
  const d = parseDentallyDate(dateStr);
  if (!d) return dateStr || '-';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

// Format diary day "Mon, 02 Jan 2025"
export const formatDiaryDay = (dayStr) => {
  if (!dayStr) return '-';
  const d = parseDentallyDate(dayStr);
  if (!d) return dayStr;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

// Compute diary entry duration in minutes
export const getDiaryDurationMins = (entry) => {
  const s = parseDentallyDate(entry.start_time);
  const e = parseDentallyDate(entry.end_time);
  if (!s || !e) return null;
  return Math.round((e - s) / 60000);
};

// Format duration minutes to "Xh Ym" string
export const formatDurationLabel = (mins) => {
  if (!mins && mins !== 0) return '-';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

// Get practitioner display name from a Dentally practitioner object
export const availPractitionerName = (p) => {
  const u = p.user || {};
  return [u.title, u.first_name, u.last_name].filter(Boolean).join(' ') || `Practitioner #${p.id}`;
};

// Static fallback list of Dentally appointment reasons.
// Prefer useDentallyReasonOptions() hook which fetches the practice's actual reasons.
export const DENTALLY_REASON_OPTIONS = [
  // Core exam
  'Exam',
  'New Patient Exam',
  'NHS Exam',
  // Hygiene
  'Scale & Polish',
  'Hygienist',
  'Exam + Scale & Polish',
  // Continuing / follow-up
  'Continuing Treatment',
  'Review',
  'Follow Up',
  // Consultations
  'Consultation',
  'Implant Consultation',
  'Orthodontic Consultation',
  'Cosmetic Consultation',
  'Invisalign Consultation',
  // Restorative
  'Restoration',
  'Crown',
  'Bridge',
  'Denture',
  'Veneer',
  // Surgical / endodontic
  'Extraction',
  'Root Canal Treatment',
  'Endodontic',
  'Periodontal Treatment',
  // Cosmetic / other
  'Whitening',
  'Implant',
  'Emergency',
  'Other',
];
